import React, {
 useContext, useState, useEffect
} from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
} from 'shared/components/ui/dialog';
import {
 Input
} from 'shared/components/ui/input';
import {
 Label
} from 'shared/components/ui/label';
import {
 Button
} from 'shared/components/ui/button';
import {
 applicationService
} from 'shared/api/services/BEXO';
import {
 Context
} from 'shared/context/GlobalState';
import {
 Application
} from './types/builderApp.types';

interface ApplicationUpdateProps {
  isOpen: boolean;
  setOpenApplicationModal: React.Dispatch<React.SetStateAction<boolean>>;
  application: Application | null;
  setApplication: React.Dispatch<React.SetStateAction<Application[]>>;
}

class ModifyAppBuilderHandler {
  validateAppName(value: string) {
    if (/\s/.test(value)) {
      return {
        isValid: false,
        message: 'App Name cannot contain spaces.',
      };
    }
    if (!/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(value)) {
      return {
        isValid: false,
        message:
          'App Name can only contain letters, numbers, and underscores, and must not start with a number.',
      };
    }
    if (value.length < 4) {
      return {
        isValid: false,
        message: 'App Name should be at least 4 characters long.',
      };
    }
    return {
      isValid: true,
      message: 'Valid App Name.',
    };
  }
}

export default function ApplicationUpdate({
  isOpen,
  setOpenApplicationModal,
  application,
  setApplication,
}: ApplicationUpdateProps) {
  const validator = new ModifyAppBuilderHandler();

  const [loading, setLoading] = useState<boolean>(false);
  const [appUpdateObject, setAppUpdateObject] = useState<Application | null>(
    application
  );
  const [validationError, setValidationError] = useState<string>('');
  const {
 addNewNotifcation
} = useContext(Context);

  useEffect(() => {
    if (application) {
      setAppUpdateObject(application);
    }
  }, [application]);

  const handleOnSubmit = async () => {
    if (!appUpdateObject) return;
    const {
 isValid, message
} = validator.validateAppName(
      appUpdateObject.full_name
    );
    if (!isValid) {
      setValidationError(message);
      return;
    }

    try {
      setLoading(true);

      // Send the update request
      await applicationService.updateApplication(
        appUpdateObject.organization_slug,
        application?.name as string,
        {
 full_name: appUpdateObject.full_name
}
      );

      // Update local state properly with returned data or current form values
      setApplication((prevApps) =>
        prevApps.map((app) =>
          app.app_id === appUpdateObject.app_id // compare by ID instead of name
            ? {
 ...app,
full_name: appUpdateObject.full_name
}
            : app
        )
      );

      addNewNotifcation('App updated successfully', 'success');
      setOpenApplicationModal(false);
    } catch (error) {
      if (error.response && error.response.status === 400) {
        addNewNotifcation(
          `Application "${appUpdateObject.full_name}" already exists`,
          'danger'
        );
      } else {
        addNewNotifcation('Error updating application', 'danger');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (value: string, field: keyof Application) => {
    if (field === 'full_name') {
      value = value.replace(/(\s|\W)/g, '_');
      const {
 isValid, message
} = validator.validateAppName(value);
      setValidationError(isValid ? '' : message);
    }

    setAppUpdateObject((prev) => (prev ? {
 ...prev,
[field]: value
} : null));
  };

  return (
      <Dialog open={isOpen} onOpenChange={() => setOpenApplicationModal(false)}>
          <DialogContent className="sm:max-w-[600px]">
              <DialogHeader>
                  <DialogTitle>Modify App</DialogTitle>
              </DialogHeader>
              <div className="grid gap-4 py-4">
                  <div className="flex flex-col gap-4">
                      <Label htmlFor="app-name">App Name</Label>
                      <Input
              id="app-name"
              value={appUpdateObject?.full_name || ''}
              onChange={(e) => handleChange(e.target.value, 'full_name')}
              placeholder="Enter App Name"
            />
                      {validationError && (
                      <p className="text-red-500 text-sm">{validationError}</p>
            )}
                  </div>
              </div>
              <DialogFooter className="mt-3">
                  <Button
            onClick={() => setOpenApplicationModal(false)}
            variant="outline"
          >
                      Cancel
                  </Button>
                  <Button
            onClick={handleOnSubmit}
            disabled={loading || validationError ? true : false}
          >
                      {loading ? 'Updating...' : 'Update App'}
                  </Button>
              </DialogFooter>
          </DialogContent>
      </Dialog>
  );
}
